<template>
  <v-tabs v-model="tab" align-tabs="center" color="secondary" class="mb-4">
    <v-tab value="selectTab" prepend-icon="mdi-tray-arrow-down">
      {{ $t("strings.opIterator-select-iteration-values") }}
    </v-tab>
    <v-tab
      value="setTab"
      prepend-icon="mdi-tray-arrow-up"
      :disabled="disabledTabStatus"
    >
      {{ $t("strings.opIterator-set-nested-inputs") }}
    </v-tab>
  </v-tabs>
  <v-tabs-window v-model="tab">
    <v-tabs-window-item value="selectTab">
      <v-row no-gutters>
        <v-col cols="5" xl="5" lg="6" md="12">
          <InputsTree :inputs="inputsData" :data="data" />
        </v-col>
        <v-col cols="7" xl="7" lg="6" md="12">
          <h4>{{ $t("strings.opIterator-iteration-params") }}</h4>
          <h6>{{ $t("strings.array-objects-to-iterate") }}</h6>
          <v-sheet
            class="overflow-y-auto pt-4"
            :max-height="data.parentContentHeight"
          >
            <v-form class="pa-2" v-model="valid">
              <InputBlock :paramValues="paramValues" @bindInput="bindInput" />
              <v-select
                v-model="innerProcess"
                class="mb-2"
                color="primary"
                :label="$t('strings.opIterator-labels-mashup-apply')"
                variant="outlined"
                density="compact"
                :items="innerMashups"
                hint=""
                item-title="name"
                item-value="id"
                return-object
                @update:modelValue="setInnerProcess"
              ></v-select>
            </v-form>
          </v-sheet>
        </v-col>
      </v-row>
    </v-tabs-window-item>
    <v-tabs-window-item value="setTab">
      <v-row no-gutters>
        <v-col cols="5" xl="5" lg="6" md="12">
          <h4>{{ $t("strings.opIterator-iterated-properties") }}</h4>
          <h6>
            {{ $t("strings.opIterator-bind-iterated-properties-to-inputs") }}
          </h6>
          <SimpleTreeView
            v-if="outputNode"
            :inputs="outputNode"
            :ondragActivation="true"
          />
        </v-col>
        <v-col cols="7" xl="7" lg="6" md="12">
          <h4>{{ $t("strings.opIterator-mashup-inputs") }}</h4>
          <h6>{{ $t("strings.opIterator-set-nested-mashup-inputs") }}</h6>

          <v-sheet
            class="overflow-y-auto py-4 px-3"
            :max-height="data.parentContentHeight"
          >
            <v-row dense>
              <v-col
                cols="12"
                v-for="(param, index) in innerProcess?.parameters"
                :key="index"
              >
                <v-text-field
                  v-if="!param.constant"
                  v-model="param.value"
                  color="primary"
                  :label="`${$t(param.label) || param.label} (${param.name})`"
                  variant="outlined"
                  density="compact"
                  @drop="onDrop($event, param, 'value')"
                  :clearable="true"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-sheet>
        </v-col>
      </v-row>
    </v-tabs-window-item>
  </v-tabs-window>
</template>

<script>
import {
  inject,
  provide,
  reactive,
  toRefs,
  watch,
  computed,
  ref,
  onMounted,
} from "vue";
import { drop } from "@/mixins/common-utils.js";
import InputsTree from "../blocks/static/InputsListTree.vue";
import InputBlock from "../blocks/static/InputBlock.vue";
import SimpleTreeView from "../blocks/static/SimpleTreeView.vue";
import axios from "axios";

export default {
  name: "OperatorConcatenateStrings",
  props: ["target", "data", "operatorInputs"],
  emits: ["updateFormData"],
  components: {
    InputsTree,
    InputBlock,
    SimpleTreeView,
  },
  setup(props, { emit }) {
    const config = inject("config");
    let apiDraw = computed(() => {
      return config.apiDraw;
    });
    const tenant = inject("tenant");
    const tab = ref(null);
    const innerMashups = ref([]);
    //Creates a deep copy, unlinked of target.userData
    const formData = reactive(
      JSON.parse(JSON.stringify(props.target.userData)) || {},
    );
    provide("formData", formData);

    onMounted(() => {
      getInnerProcesses(); //Retrieves inner Mashups
    });

    const state = reactive({
      valid: inject("valid"),
      innerProcess: formData.innerProcess,
      outputNode: { [props.target.id]: formData.outputNode },
      paramValues: formData.parameters[0],
    });
    const { valid, innerProcess, outputNode, paramValues } = toRefs(state);

    watch(
      formData,
      (newFormData) => {
        emit("updateFormData", newFormData);
      },
      { deep: true },
    );

    //Computes and return inputs linked
    let inputsData = computed(() => {
      return props.operatorInputs;
    });
    //Computes and return first tab inputs couple status
    let disabledTabStatus = computed(() => {
      return !innerProcess.value || !outputNode.value[props.target.id];
    });

    async function getInnerProcesses() {
      try {
        let path = window.location.pathname;
        let parts = path.split("/");
        let versionId = parts[parts.indexOf("v") + 1];
        const response = await axios.get(
          apiDraw.value +
            "/" +
            tenant +
            "/draw/" +
            versionId +
            "/inner-processes",
          { withCredentials: true },
        );
        innerMashups.value = response.data;
      } catch (error) {
        console.error(error);
      }
      return null;
    }

    function setInnerProcess(val) {
      formData.innerProcess = val;
    }

    /**
     * The bindInput function links an input element to a specific parameter and configures the options for that parameter.
     * @param {Object} input - The input object from which unique keys are extracted.
     * @param {string} bindedElement - The name property value in a parameter object.
     *  Because selectParams is a reference to the parameter object within formData.parameters, these changes are also reflected in formData.parameters.
     *  As a result, the input element is linked to the specified parameter and its options are configured based on the input.
     */
    function bindInput(input) {
      if (input) {
        outputNode.value = { [props.target.id]: input[0] };
        formData.outputNode = input[0];
      } else {
        outputNode.value = {};
        formData.outputNode = {};
      }
    }

    const onDrop = (event, item, targetName) => {
      event.stopPropagation();
      try {
        item.value = "";
        drop(event, targetName, item);
        valid.value = true;
      } catch (error) {
        console.error("Error performing onDrop: ", error);
      }
    };

    return {
      inputsData,
      formData,
      ...toRefs(formData),
      valid,
      paramValues,
      tab,
      apiDraw,
      innerProcess,
      outputNode,
      innerMashups,
      bindInput,
      onDrop,
      state,
      setInnerProcess,
      disabledTabStatus,
    };
  },
};
</script>
